/**
 * Customer Panel UI Components
 * 
 * Tekrar eden UI component'leri - OOP prensiplerine uygun
 * WCAG/W3C erişilebilirlik standartlarına uygun
 */

import React from 'react';
import { createErrorMessageAriaAttributes } from './accessibility';

/**
 * Error message component
 */
interface ErrorMessageProps {
  message: string;
  fieldId?: string;
  className?: string;
}

export function ErrorMessage({ message, fieldId, className = '' }: ErrorMessageProps) {
  const errorId = fieldId ? `${fieldId}-error` : undefined;
  const ariaAttributes = fieldId ? createErrorMessageAriaAttributes(fieldId) : {};

  return (
    <div
      className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}
      role="alert"
      {...ariaAttributes}
    >
      <p className="text-sm text-red-800">{message}</p>
    </div>
  );
}

/**
 * Success message component
 */
interface SuccessMessageProps {
  message: string;
  className?: string;
}

export function SuccessMessage({ message, className = '' }: SuccessMessageProps) {
  return (
    <div
      className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <p className="text-sm text-green-800">{message}</p>
    </div>
  );
}

/**
 * Loading spinner component
 */
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  label?: string;
}

export function LoadingSpinner({ size = 'md', className = '', label = 'Loading...' }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  return (
    <div className={`flex items-center justify-center ${className}`} role="status" aria-label={label}>
      <div
        className={`animate-spin rounded-full border-b-2 border-gray-900 ${sizeClasses[size]}`}
        aria-hidden="true"
      />
      <span className="sr-only">{label}</span>
    </div>
  );
}

/**
 * Form field wrapper component
 */
interface FormFieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string | null;
  children: React.ReactNode;
  className?: string;
}

export function FormField({ id, label, required = false, error, children, className = '' }: FormFieldProps) {
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className={className}>
      <label htmlFor={id} className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">
        {label}
        {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
      </label>
      {children}
      {error && (
        <p id={errorId} className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

